import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardTitle, FormGroup, Label, Input, Spinner, Table } from 'reactstrap';
import { PaymentSystem } from '../../../models';
import AccountPspCredentialsApi from '../../../services/rest/AccountPspCredentials';
import DevicesApi from '../../../services/rest/Devices';
import { credentialBatchSummaryText, formatIsoDateTime } from '../../../utils/credentialDisplay';

const normalizePaymentSystemName = (name) => String(name || '').toLowerCase();

const defaultPayloadByName = (name) => {
    const normalized = normalizePaymentSystemName(name);
    if (!normalized) {
        return {};
    }
    if (normalized.includes('stripe')) {
        return { account_id: '', api_key: '' };
    }
    if (normalized.includes('wallee')) {
        return { space_id: '', user_id: '', secret: '' };
    }
    if (normalized.includes('sumup')) {
        return { email: '', refresh_token: '', access_token: '' };
    }
    return {};
};

const payloadFieldsByName = (name) => {
    const normalized = normalizePaymentSystemName(name);
    if (!normalized) {
        return [];
    }
    if (normalized.includes('stripe')) {
        return [
            {
                key: 'account_id',
                labelKey: 'accounts.credentials_stripe_account_id',
                placeholderKey: 'accounts.credentials_stripe_account_id_placeholder',
                type: 'text',
                required: false
            },
            {
                key: 'api_key',
                labelKey: 'accounts.credentials_stripe_api_key',
                placeholderKey: 'accounts.credentials_stripe_api_key_placeholder',
                type: 'password'
            }
        ];
    }
    if (normalized.includes('wallee')) {
        return [
            {
                key: 'space_id',
                labelKey: 'accounts.credentials_wallee_space_id',
                placeholderKey: 'accounts.credentials_wallee_space_id_placeholder',
                type: 'text'
            },
            {
                key: 'user_id',
                labelKey: 'accounts.credentials_wallee_user_id',
                placeholderKey: 'accounts.credentials_wallee_user_id_placeholder',
                type: 'text'
            },
            {
                key: 'secret',
                labelKey: 'accounts.credentials_wallee_secret',
                placeholderKey: 'accounts.credentials_wallee_secret_placeholder',
                type: 'password'
            }
        ];
    }
    if (normalized.includes('sumup')) {
        return [
            {
                key: 'email',
                labelKey: 'accounts.credentials_sumup_email',
                placeholderKey: 'accounts.credentials_sumup_email_placeholder',
                type: 'email'
            },
            {
                key: 'refresh_token',
                labelKey: 'accounts.credentials_sumup_refresh_token',
                placeholderKey: 'accounts.credentials_sumup_refresh_token_placeholder',
                type: 'password'
            },
            {
                key: 'access_token',
                labelKey: 'accounts.credentials_sumup_access_token',
                placeholderKey: 'accounts.credentials_sumup_access_token_placeholder',
                type: 'password'
            }
        ];
    }
    return [];
};

const isSupportedPaymentSystem = (name) => {
    const normalized = normalizePaymentSystemName(name);
    return normalized === normalizePaymentSystemName(PaymentSystem.PAYMENT_SYSTEM_STRIPE_ONLINE)
        || normalized === normalizePaymentSystemName(PaymentSystem.PAYMENT_SYSTEM_SUMUP_ONLINE)
        || normalized === normalizePaymentSystemName(PaymentSystem.PAYMENT_SYSTEM_WALLEE_ONLINE);
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AccountCredentialsForm = ({ accountId }) => {
    const { t } = useTranslation();
    const paymentSystems = useSelector((state) => state.payment_systems?.payment_systems || []);
    const [credentials, setCredentials] = useState([]);
    const [label, setLabel] = useState('');
    const [paymentSystemId, setPaymentSystemId] = useState('');
    const [environment, setEnvironment] = useState('prod');
    const [payload, setPayload] = useState({});
    const [error, setError] = useState('');
    const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
    const [saving, setSaving] = useState(false);
    const [devicesByCredentialId, setDevicesByCredentialId] = useState({});

    const availablePaymentSystems = paymentSystems.filter((ps) => isSupportedPaymentSystem(ps.name));
    const selectedPaymentSystem = availablePaymentSystems.find((ps) => ps.id === paymentSystemId);
    const payloadFields = payloadFieldsByName(selectedPaymentSystem?.name);
    const visibleCredentials = (Array.isArray(credentials) ? credentials : []).filter(
        (credential) => credential && (credential.id || credential.label || credential.payment_system_id)
    );

    const load = useCallback(() => {
        setIsLoadingCredentials(true);
        Promise.all([
            AccountPspCredentialsApi.list({ account_id: accountId }),
            DevicesApi.list({ account_id: accountId }),
        ])
            .then(([credData, devData]) => {
                const creds = Array.isArray(credData.account_psp_credentials) ? credData.account_psp_credentials : [];
                const devices = Array.isArray(devData.devices) ? devData.devices : [];
                const map = {};
                devices.forEach((device) => {
                    const cid = device.credential_id;
                    if (!cid) {
                        return;
                    }
                    if (!map[cid]) {
                        map[cid] = [];
                    }
                    map[cid].push(device);
                });
                setCredentials(creds);
                setDevicesByCredentialId(map);
            })
            .catch(() => {
                setCredentials([]);
                setDevicesByCredentialId({});
            })
            .finally(() => setIsLoadingCredentials(false));
    }, [accountId]);

    useEffect(() => {
        if (accountId) {
            load();
        }
    }, [accountId, load]);

    const onPaymentSystemChange = (value) => {
        setPaymentSystemId(value);
        const paymentSystem = availablePaymentSystems.find((ps) => ps.id === value);
        setPayload(defaultPayloadByName(paymentSystem?.name));
    };

    const onCreate = () => {
        if (!selectedPaymentSystem) {
            setError(t('accounts.credentials_payment_system_not_supported'));
            return;
        }
        const emptyFields = payloadFields.filter(
            (field) => field.required !== false && !String(payload?.[field.key] || '').trim()
        );
        if (emptyFields.length > 0) {
            setError(t('accounts.credentials_required_fields'));
            return;
        }
        const hasInvalidEmail = payloadFields.some(
            (field) => field.type === 'email' && !EMAIL_REGEX.test(String(payload?.[field.key] || '').trim())
        );
        if (hasInvalidEmail) {
            setError(t('accounts.credentials_invalid_email'));
            return;
        }
        const payloadToSend = payloadFields.reduce((result, field) => {
            const value = String(payload?.[field.key] || '').trim();
            if (!value && field.required === false) {
                result[field.key] = null;
            } else {
                result[field.key] = value;
            }
            return result;
        }, {});

        setSaving(true);
        setError('');
        AccountPspCredentialsApi.create({
            account_id: accountId,
            payment_system_id: paymentSystemId,
            label,
            environment,
            status: 'active',
            payload: payloadToSend
        })
            .then(() => {
                setLabel('');
                setPaymentSystemId('');
                setPayload({});
                load();
            })
            .catch((err) => setError(err.message))
            .finally(() => setSaving(false));
    };

    const onDelete = (id) => {
        AccountPspCredentialsApi.delete(id)
            .then(() => load())
            .catch((err) => setError(err.message));
    };

    return (
        <Card>
            <CardBody>
                <CardTitle>{t('accounts.credentials_title')}</CardTitle>
                {!!error && <small className="text-danger d-block mb-2">{error}</small>}
                {isLoadingCredentials && (
                    <div className="d-flex align-items-center mb-2">
                        <Spinner size="sm" color="secondary" className="mr-2" />
                        <small className="text-muted">{t('accounts.credentials_loading')}</small>
                    </div>
                )}
                {!isLoadingCredentials && visibleCredentials.length === 0 && (
                    <small className="text-muted d-block mb-3">{t('accounts.credentials_empty')}</small>
                )}
                {visibleCredentials.length > 0 && (
                    <Table size="sm" striped responsive>
                        <thead>
                            <tr>
                                <th>{t('accounts.credentials_label')}</th>
                                <th>{t('accounts.credentials_payment_system')}</th>
                                <th>{t('accounts.credentials_environment')}</th>
                                <th>{t('accounts.credentials_token_expires_at')}</th>
                                <th>{t('accounts.credentials_batch_status')}</th>
                                <th>{t('accounts.credentials_linked_devices')}</th>
                                <th>{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleCredentials.map((credential) => {
                                const paymentSystem = paymentSystems.find((ps) => ps.id === credential.payment_system_id);
                                const linked = devicesByCredentialId[credential.id] || [];
                                return (
                                    <tr key={credential.id}>
                                        <td>{credential.label}</td>
                                        <td>{paymentSystem?.name || credential.payment_system_id}</td>
                                        <td>{credential.environment}</td>
                                        <td>{formatIsoDateTime(credential.token_expires_at) || '—'}</td>
                                        <td title={credential.last_batch_check_detail || ''}>
                                            {credentialBatchSummaryText(credential, t)}
                                        </td>
                                        <td>
                                            {linked.length === 0 ? (
                                                <span className="text-muted">{t('accounts.credentials_no_linked_devices')}</span>
                                            ) : (
                                                <ul className="list-unstyled mb-0 small">
                                                    {linked.map((device) => (
                                                        <li key={device.id}>
                                                            <Link to={`/devices/edit/${device.id}`}>
                                                                {device.name || device.identifier || device.id}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </td>
                                        <td>
                                            <Button size="sm" color="danger" onClick={() => onDelete(credential.id)}>
                                                {t('accounts.credentials_delete')}
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}

                <CardTitle className="mt-4">{t('accounts.credentials_add_title')}</CardTitle>
                <FormGroup>
                    <Label>{t('accounts.credentials_label')}</Label>
                    <Input
                        id="credential_label"
                        name="credential_label"
                        autoComplete="off"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>{t('accounts.credentials_payment_system')}</Label>
                    <Input
                        id="credential_payment_system"
                        name="credential_payment_system"
                        autoComplete="off"
                        type="select"
                        value={paymentSystemId}
                        onChange={(e) => onPaymentSystemChange(e.target.value)}
                    >
                        <option value="">---</option>
                        {availablePaymentSystems.map((ps) => (
                            <option key={ps.id} value={ps.id}>{ps.name}</option>
                        ))}
                    </Input>
                </FormGroup>
                <FormGroup>
                    <Label>{t('accounts.credentials_environment')}</Label>
                    <Input
                        id="credential_environment"
                        name="credential_environment"
                        autoComplete="off"
                        type="select"
                        value={environment}
                        onChange={(e) => setEnvironment(e.target.value)}
                    >
                        <option value="prod">prod</option>
                        <option value="staging">staging</option>
                        <option value="dev">dev</option>
                    </Input>
                </FormGroup>
                {payloadFields.map((field) => (
                    <FormGroup key={field.key}>
                        <Label>{t(field.labelKey)}</Label>
                        <Input
                            type={field.type}
                            id={`credential_${field.key}`}
                            name={`credential_${field.key}`}
                            autoComplete="off"
                            placeholder={t(field.placeholderKey)}
                            value={payload?.[field.key] || ''}
                            onChange={(e) => {
                                const { value } = e.target;
                                setPayload((current) => ({ ...current, [field.key]: value }));
                            }}
                        />
                    </FormGroup>
                ))}
                <Button color="primary" disabled={saving || !label || !paymentSystemId} onClick={onCreate}>
                    {t('accounts.credentials_add')}
                </Button>
            </CardBody>
        </Card>
    );
};

export default AccountCredentialsForm;
